import React, { useEffect, useMemo, useState } from 'react'
import { Loader2, Search, X } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import Field from './Field'
import CopyField from './CopyField'
import MemberRow from './MemberRow'
import MemberListHeader from './MemberListHeader'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  listMembers,
  updateMemberRole,
  removeMember,
  getWorkspaceCredits,
  updateMemberAllocation,
  ApiError,
} from '../services/settingsApi'
import { roleOrder } from '../types'
import type { MemberRecord, WorkspaceCreditSummary } from '../types'
import type { WorkspaceSummary } from '@/features/onboarding/services/workspaceApi'

type Props = { workspace: WorkspaceSummary }

const WorkspaceSection: React.FC<Props> = ({ workspace }) => {
  const { currentTheme: t } = useTheme()
  const [members, setMembers] = useState<MemberRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [credits, setCredits] = useState<WorkspaceCreditSummary | null>(null)
  const [creditsLoading, setCreditsLoading] = useState(true)
  const [allocationErrors, setAllocationErrors] = useState<Record<string, string>>({})
  const [memberPendingRemoval, setMemberPendingRemoval] = useState<MemberRecord | null>(null)
  const [allMembersOpen, setAllMembersOpen] = useState(false)
  const [memberSearch, setMemberSearch] = useState('')

  const VISIBLE_MEMBER_LIMIT = 5

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase()
    if (!q) return members
    return members.filter((m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
  }, [members, memberSearch])

  const myUserId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').id as string } catch { return '' }
  })()

  const canManage = workspace.role === 'owner' || workspace.role === 'admin'
  const canAllocate = workspace.role === 'owner'
  const allMembersUnlimited = members.length > 0 && members.every((m) => m.role === 'owner' || m.aiCreditAllocation === null)

  useEffect(() => {
    if (!canManage) { setLoading(false); setCreditsLoading(false); return }
    listMembers(workspace.id)
      .then((m) => setMembers(m.sort((a, b) => roleOrder(a.role) - roleOrder(b.role))))
      .catch(() => {})
      .finally(() => setLoading(false))
    getWorkspaceCredits(workspace.id)
      .then(setCredits)
      .catch(() => {})
      .finally(() => setCreditsLoading(false))
  }, [workspace.id, canManage])

  const handleRoleChange = async (membershipId: string, newRole: 'admin' | 'member') => {
    try {
      const updated = await updateMemberRole(membershipId, newRole)
      setMembers((prev) =>
        prev.map((m) => (m.id === membershipId ? { ...m, role: updated.role } : m))
          .sort((a, b) => roleOrder(a.role) - roleOrder(b.role)),
      )
    } catch {}
  }

  const handleRemove = async (membershipId: string) => {
    try {
      await removeMember(membershipId)
      setMembers((prev) => prev.filter((m) => m.id !== membershipId))
    } catch {}
  }

  const confirmRemove = async () => {
    if (!memberPendingRemoval) return
    await handleRemove(memberPendingRemoval.id)
    setMemberPendingRemoval(null)
  }

  const handleAllocationSave = async (membershipId: string, allocation: number | null) => {
    setAllocationErrors((prev) => {
      const next = { ...prev }
      delete next[membershipId]
      return next
    })
    try {
      const updated = await updateMemberAllocation(membershipId, allocation)
      setMembers((prev) =>
        prev.map((m) =>
          m.id === membershipId
            ? { ...m, aiCreditAllocation: updated.aiCreditAllocation, aiCreditsUsedThisPeriod: updated.aiCreditsUsedThisPeriod }
            : m,
        ),
      )
      // The pool's unallocated figure shifted — refresh the summary.
      getWorkspaceCredits(workspace.id).then(setCredits).catch(() => {})
    } catch (err) {
      let message = err instanceof Error ? err.message : 'Failed to update allocation'
      if (err instanceof ApiError && err.code === 'allocation_exceeds_pool') {
        const details = err.details as { unallocated?: number } | undefined
        if (typeof details?.unallocated === 'number') {
          message = `${message} (${details.unallocated.toLocaleString()} credits unallocated)`
        }
      }
      setAllocationErrors((prev) => ({ ...prev, [membershipId]: message }))
      throw err
    }
  }

  const formatResetDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    } catch {
      return iso
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-[18px]">
          <Field label="Workspace name" value={workspace.name} readOnly={!canManage} />
          {workspace.description
            ? <Field label="Description" value={workspace.description} readOnly={!canManage} />
            : <div />}
          <CopyField label="Invite code" value={workspace.joiningId} />
          <Field label="Your role" value={workspace.role.charAt(0).toUpperCase() + workspace.role.slice(1)} readOnly />
        </div>
      </div>

      {canManage && (
        <div>
          <h2 className="text-sm font-bold mb-[10px]" style={{ color: t.text }}>Plan &amp; Credits</h2>
          {creditsLoading ? (
            <div className="flex justify-center py-6 rounded-2xl" style={{ border: `1px solid ${t.border}` }}>
              <Loader2 size={18} className="animate-spin" style={{ color: t.mutedText }} />
            </div>
          ) : credits ? (
            <div
              className="rounded-2xl p-[18px] grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4"
              style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
            >
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>Plan</p>
                <p className="text-[13.5px] font-semibold mt-0.5" style={{ color: t.text }}>
                  {credits.planName ?? 'No plan assigned'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>Seats</p>
                <p className="text-[13.5px] font-semibold mt-0.5" style={{ color: t.text }}>
                  {credits.seats ?? '—'}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>Monthly pool</p>
                <p className="text-[13.5px] font-semibold mt-0.5" style={{ color: t.text }}>
                  {credits.monthlyAiCredits.toLocaleString()}
                </p>
              </div>
              {!allMembersUnlimited && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>Allocated</p>
                  <p className="text-[13.5px] font-semibold mt-0.5" style={{ color: t.text }}>
                    {credits.allocated.toLocaleString()}
                  </p>
                </div>
              )}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>Unallocated</p>
                <p className="text-[13.5px] font-semibold mt-0.5" style={{ color: t.text }}>
                  {credits.unallocated.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: t.mutedText }}>Resets on</p>
                <p className="text-[13.5px] font-semibold mt-0.5" style={{ color: t.text }}>
                  {formatResetDate(credits.periodResetAt)}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {canManage && (
        <div>
          <div className="mb-[18px]">
            <h2 className="text-sm font-bold" style={{ color: t.text }}>Members</h2>
            <p className="text-[12.5px] mt-[3px]" style={{ color: t.mutedText }}>
              {loading ? 'Loading…' : `${members.length} member${members.length !== 1 ? 's' : ''} in this workspace`}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} />
            </div>
          ) : (
            <div className="rounded-[14px] overflow-hidden" style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}>
              <MemberListHeader />
              {members.slice(0, VISIBLE_MEMBER_LIMIT).map((m, i, arr) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  isMe={m.userId === myUserId}
                  isLast={i === arr.length - 1 && members.length <= VISIBLE_MEMBER_LIMIT}
                  myRole={workspace.role}
                  canAllocate={canAllocate}
                  {...(allocationErrors[m.id] ? { allocationError: allocationErrors[m.id] } : {})}
                  onRoleChange={handleRoleChange}
                  onAllocationSave={handleAllocationSave}
                  onRequestRemove={setMemberPendingRemoval}
                />
              ))}
              {members.length > VISIBLE_MEMBER_LIMIT && (
                <button
                  type="button"
                  onClick={() => setAllMembersOpen(true)}
                  className="w-full text-center py-2.5 text-[12.5px] font-semibold transition-colors"
                  style={{ color: t.accent, borderTop: `1px solid ${t.borderSecondary}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.hoverBackground }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent' }}
                >
                  View all {members.length} members
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog open={memberPendingRemoval !== null} onOpenChange={(open) => { if (!open) setMemberPendingRemoval(null) }}>
        <DialogContent
          className="py-6 px-8 rounded-2xl shadow-xl max-w-sm w-full border-none"
          style={{ background: t.secondary, color: t.text }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-center" style={{ color: t.text }}>
              Remove {memberPendingRemoval?.name} from this workspace?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-center mt-1" style={{ color: t.mutedText }}>
            They will lose access immediately. You can invite them again later.
          </p>
          <DialogFooter className="flex justify-center gap-3 mt-6 flex-row sm:justify-center">
            <Button
              variant="secondary"
              onClick={() => setMemberPendingRemoval(null)}
              className="px-5 py-2 rounded-lg font-medium h-auto text-sm"
              style={{ background: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmRemove()}
              className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium h-auto text-sm"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={allMembersOpen}
        onOpenChange={(open) => { setAllMembersOpen(open); if (!open) setMemberSearch('') }}
      >
        <DialogContent
          className="p-0 rounded-2xl shadow-xl max-w-3xl w-[92vw] border-none flex flex-col max-h-[80vh]"
          style={{ background: t.secondary, color: t.text }}
        >
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="text-lg font-semibold" style={{ color: t.text }}>
              All members
            </DialogTitle>
          </DialogHeader>

          <div className="px-6 pt-4">
            <div className="relative">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: t.mutedText }}
              />
              <input
                autoFocus
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search by name or email"
                className="w-full h-9 rounded-lg pl-9 pr-8 text-sm outline-none transition"
                style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}`, color: t.text }}
              />
              {memberSearch && (
                <button
                  type="button"
                  onClick={() => setMemberSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2"
                  style={{ color: t.mutedText }}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="px-6 pb-6 pt-4 min-h-0 flex-1 overflow-y-auto custom-scrollbar">
            <div className="rounded-[14px] overflow-hidden" style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}>
              <MemberListHeader />
              {filteredMembers.length === 0 ? (
                <div className="py-10 text-center text-sm" style={{ color: t.mutedText }}>
                  No members match "{memberSearch}"
                </div>
              ) : (
                filteredMembers.map((m, i, arr) => (
                  <MemberRow
                    key={m.id}
                    member={m}
                    isMe={m.userId === myUserId}
                    isLast={i === arr.length - 1}
                    myRole={workspace.role}
                    canAllocate={canAllocate}
                    {...(allocationErrors[m.id] ? { allocationError: allocationErrors[m.id] } : {})}
                    onRoleChange={handleRoleChange}
                    onAllocationSave={handleAllocationSave}
                    onRequestRemove={setMemberPendingRemoval}
                  />
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(WorkspaceSection)
